import { request } from './request';
import * as PATHS from '@/constants/path';
import { URLParamBuilder } from '@/utils/functions';
import { GetCustomersRequest } from '@/typings/api/getCustomers';
import {
  CreateCSVLeadSourceRequest,
  CreateCSVLeadsRequest,
  CreateLeadRequest,
  DeleteLeadSourceRequest,
  FetchAssigneesRequest,
  FetchLeadSourceRequest,
  FetchServiceRequest,
  GetLeadSourceRequest,
} from '@/typings/api/lead';


// region Fetch Leads
type FetchLeads = {
  url?: string,
  query?: {}
}

export function fetchLeads({
                             url,
                             query = {},
                           }: FetchLeads) {
  const finalUrl = url || URLParamBuilder(PATHS.GET_LEADS, {
    queryString: query,
  });
  return request<GetCustomersRequest['response']>(finalUrl, {
    method: 'GET',
  });
}


// region Fetch Leads

export function fetchCustomersRequest({
                                        url,
                                        query = {},
                                      }: FetchLeads) {
  const finalUrl = url || URLParamBuilder(PATHS.GET_ALL_CUSTOMERS_API_PATH, {
    queryString: { ...query, skip_total_size: 1 },
  });
  return request<GetCustomersRequest['response']>(finalUrl, {
    method: 'GET',
  });
}


export function fetchSingleLeadDetails({
                                         params,
                                       }) {
  return request<GetCustomersRequest['response']>(URLParamBuilder(PATHS.GET_LEAD, {
    params,
  }), {
    method: 'GET',
  });
}

export function fetchLinkedLeadsRequest({
                                          params,
                                        }: FetchLeads) {
  return request<GetCustomersRequest['response']>(URLParamBuilder(PATHS.GET_LEAD_LINKED_LEADS, {
    params,
  }), {
    method: 'GET',
  });
}

// region Edit Lead
type EditLeadsRequest = {
  params: {
    leadId: string
  },
  url?: string,
  data: {
    [key: string]: any
  }
}

export function editLeadsRequest({
                                   params,
                                   url,
                                   query,
                                   data = {},
                                 }: EditLeadsRequest) {
  const finalUrl = url || URLParamBuilder(PATHS.EDIT_LEAD, {
    params,
    queryString: query
  });
  return request(finalUrl, {
    method: 'PATCH',
    absoluteURL: url,
    data,
  });
}

export function fetchAssignees(
  { params = {}, query = {} }) {
  const finalUrl = URLParamBuilder(PATHS.GET_ALL_USER_API_PATH, {
    params,
    queryString: query,
  }, false);
  return request<FetchAssigneesRequest['response']>(finalUrl, {
    method: 'GET',
  });
}


export function createLeadRequest({ data, params }) {
  const url = URLParamBuilder(PATHS.CREATE_LEAD_MANUAL_SOURCE, {
    params,
  });
  return request<CreateLeadRequest['response']>(url, {
    method: 'POST',
    data,
  });
}

export function createLeadWithoutSource({ data }) {
  const url = URLParamBuilder(PATHS.CREATE_LEAD);
  return request<CreateLeadRequest['response']>(url, {
    method: 'POST',
    data,
  });
}

export function assignLeadRequest({ payload }) {
  return request<CreateLeadRequest['response']>(PATHS.ASSIGN_LEAD, {
    method: 'POST',
    data: payload,
  });
}

export function archiveLeadRequest({ payload }) {
  return request<CreateLeadRequest['response']>(PATHS.ARCHIVE_LEAD, {
    method: 'POST',
    data: payload,
  });
}

export function postCommentToLeadRequest({ data, params }) {
  const url = URLParamBuilder(PATHS.COMMENT_ON_LEAD, { params });
  return request(url, {
    method: 'POST',
    data,
  });
}

export function fetchLeadSources(
  params = {}) {
  const finalUrl = URLParamBuilder(PATHS.FETCH_LEAD_SOURCES, {
    params,
  }, false);
  return request<FetchLeadSourceRequest['response']>(finalUrl, {
    method: 'GET',
  });
}


// region Managed Lead Sources (Laravel api/v4/partner/lead-sources)
export function fetchManagedLeadSources() {
  return request(PATHS.MANAGED_LEAD_SOURCES, {
    method: 'GET',
  });
}

export function editLeadSourceRequest({
                                        params,
                                        data = {},
                                      }: { params: { sourceId: string }, data: Record<string, unknown> }) {
  const url = URLParamBuilder(PATHS.MANAGED_LEAD_SOURCE, {
    params,
  });
  return request(url, {
    method: 'PATCH',
    data,
  });
}

/**
 * Promote a lead source to the partner's default. Not part of
 * editLeadSourceRequest because the write lands on the partner, not on the lead
 * source, and carries its own permission gate.
 */
export function setDefaultLeadSourceRequest({ params }: { params: { sourceId: string } }) {
  const url = URLParamBuilder(PATHS.MANAGED_LEAD_SOURCE_DEFAULT, { params });
  return request(url, {
    method: 'POST',
  });
}
// endregion

export function fetchServices(
  params = {}) {
  const finalUrl = URLParamBuilder(PATHS.FETCH_LEAD_SERVICES, {
    params,
  }, false);
  return request<FetchServiceRequest['response']>(finalUrl, {
    method: 'GET',
  });
}

export function fetchLeadFields() {
  const url = URLParamBuilder(PATHS.GET_LEAD_FIELDS);
  return request(url, {
    method: 'GET',
  });
}


export function createCSVLeadSourceRequest({ data }) {
  const url = PATHS.CREATE_LEAD_SOURCE_CSV;
  return request<CreateCSVLeadSourceRequest['response']>(url, {
    method: 'POST',
    data,
  });
}

export function createCSVLeadsRequest({ data }) {
  const url = URLParamBuilder(PATHS.CREATE_LEADS_CSV, {
    params: { sourceId: data?.sourceId },
  });
  return request<CreateCSVLeadsRequest['response']>(url, {
    method: 'POST',
    data,
  });
}

export function deleteLeadSource({ params }: DeleteLeadSourceRequest['parameters']) {
  const url = URLParamBuilder(PATHS.DELETE_LEAD_SOURCE_CSV, {
    params: { sourceId: params.sourceId },
  });
  return request<DeleteLeadSourceRequest['response']>(url, {
    method: 'DELETE',
  });
}

export function getLeadSource({ params }: GetLeadSourceRequest['parameters']) {
  return request<GetLeadSourceRequest['response']>(URLParamBuilder(PATHS.GET_LEAD_SOURCE, {
    params: { sourceId: params.sourceId },
  }), {
    method: 'GET',
  });
}

export function updateCSVLeadSourceRequest({ data }) {
  const url = URLParamBuilder(PATHS.UPDATE_LEAD_SOURCE_CSV, {
    params: { sourceId: data.id },
  });
  return request<CreateCSVLeadSourceRequest['response']>(url, {
    method: 'PATCH',
    data,
  });
}


export function fetchColdProspectsOverviewRequest() {
  const url = URLParamBuilder(PATHS.GET_COLD_PROSPECTS_OVERVIEW, {});
  return request<GetCustomersRequest['response']>(url, {
    method: 'GET',
  });
}


export function removeLeadFileRequest({ params }) {
  const url = URLParamBuilder(PATHS.REMOVE_LEAD_FILE, { params });
  return request<GetCustomersRequest['response']>(url, {
    method: 'DELETE',
  });
}

export function removeCustomerFileRequest({ params }) {
  const url = URLParamBuilder(PATHS.REMOVE_CUSTOMER_FILE, { params });
  return request<GetCustomersRequest['response']>(url, {
    method: 'DELETE',
  });
}

export function fetchLeadActivities({
                                      params,
                                      url = null,
                                      query = {},
                                    }) {
  // If a direct URL is provided (for pagination), use it
  if (url) {
    return request(url, {
      method: 'GET',
    });
  }

  const finalUrl = URLParamBuilder(PATHS.FETCH_LEAD_ACTIVITIES, {
    params, queryString: {
      page_size: 100,
      ...query,
    },
  });
  return request(finalUrl, {
    method: 'GET',
  });
}

export function deleteLeadRequest({ params }: { params: { leadId: string } }) {
  const url = URLParamBuilder(PATHS.EDIT_LEAD, { params });
  return request(url, { method: 'DELETE' });
}

export function bulkDeleteLeadsRequest({ ids }: { ids: string[] }) {
  return request(PATHS.GET_LEADS, { method: 'DELETE', data: { ids } });
}

export type BulkLeadActionType =
  | 'archive'
  | 'set_priority'
  | 'set_state'
  | 'set_assignee'
  | 'set_product_group'
  | 'set_extra_fields';

export type BulkLeadExtraField = {
  uuid: string;
  value: any;
};

/**
 * Bulk operation on multiple leads.
 *
 * @param action - One of: archive, set_priority, set_state, set_assignee,
 *                         set_product_group, set_extra_fields
 * @param ids - Array of lead UUIDs (max 100)
 * @param value - Required for set_* actions; for set_extra_fields must be BulkLeadExtraField[]
 */
export function bulkLeadAction({
  action,
  ids,
  value,
}: {
  action: BulkLeadActionType;
  ids: string[];
  value?: any;
}) {
  return request(PATHS.BULK_LEADS, {
    method: 'POST',
    data: { action, ids, value },
  });
}
